import React, { createContext, useContext, useMemo } from 'react'
import useMedia from 'use-media'

import { breakpoints } from 'styles/media'

type MediaQueriesMatches = {
  [key in keyof typeof breakpoints]: boolean
}

export const MediaQueryContext = createContext<MediaQueriesMatches>(null)

const mediaQueries = {
  giant: `(min-width: ${1250}px)`,
  desktop: `(min-width: ${1080}px)`,
  tablet: `(min-width: ${768}px)`,
  phone: `(min-width: ${450}px)`,
  phoneMd: `(min-width: ${375}px)`,
  sm: `(min-width: ${576}px)`,
  md: `(min-width: ${768}px)`,
  lg: `(min-width: ${992}px)`,
  xl: `(min-width: ${1200}px)`,
  xxl: `(min-width: ${1400}px)`,
  xxxl: `(min-width: ${1600}px)`,
  xwide: `(min-width: ${1920}px)`,
}

export const MediaQueryProvider: React.FC = ({ children }) => {
  const giant = useMedia(mediaQueries.giant)
  const desktop = useMedia(mediaQueries.desktop)
  const tablet = useMedia(mediaQueries.tablet)
  const phone = useMedia(mediaQueries.phone)
  const phoneMd = useMedia(mediaQueries.phoneMd)
  const sm = useMedia(mediaQueries.sm)
  const md = useMedia(mediaQueries.md)
  const lg = useMedia(mediaQueries.lg)
  const xl = useMedia(mediaQueries.xl)
  const xxl = useMedia(mediaQueries.xxl)
  const xxxl = useMedia(mediaQueries.xxxl)
  const xwide = useMedia(mediaQueries.xwide)

  const value = useMemo(
    () => ({
      giant,
      desktop,
      tablet,
      phone,
      phoneMd,
      sm,
      md,
      lg,
      xl,
      xxl,
      xxxl,
      xwide,
    }),
    [giant, desktop, tablet, phone, phoneMd, sm, md, lg, xl, xxl, xxxl, xwide]
  )

  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

const defaultMockValues = {
  giant: true,
  desktop: true,
  tablet: true,
  phone: true,
  phoneMd: true,
  sm: true,
  md: true,
  lg: true,
  xl: true,
  xxl: true,
  xxxl: true,
  xwide: true,
} as const

export const mockValuesMobile: MediaQueriesMatches = {
  giant: false,
  desktop: false,
  tablet: false,
  phone: true,
  phoneMd: true,
  sm: true,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  xxxl: false,
  xwide: false,
} as const

export const MockMediaQueryProvider: React.FC<{
  value?: MediaQueriesMatches
}> = ({ value = defaultMockValues, children }) => {
  return (
    <MediaQueryContext.Provider value={value}>
      {children}
    </MediaQueryContext.Provider>
  )
}

export const useMediaQueryContext = () => useContext(MediaQueryContext)
